/**
 * The Glass — the vocabulary pane (wave glass-2, #793; THE GLASS ruled #786).
 *
 * A sub-route of The Glass: the ratified glossary, the ruled-out words, and
 * the retired ids, every element rendered VERBATIM from the packaged canon
 * (GLASS_CANON.vocabulary, generated at build time from the three canon files
 * this page footnotes):
 *   GLOSSARY  — engineering/contract/glossary.yaml (42 ratified terms at
 *               ratification; the packaged count is the file's own).
 *   RULED-OUT — engineering/contract/glossary-ruled-out.yaml — rendered AS
 *               the record of what was ruled out, with each word's ruled
 *               disposition. Nothing here defines a ruled-out word or
 *               presents one as a business term.
 *   RETIRED   — engineering/contract/retired.ts — what a dead id meant, so a
 *               reader who finds it in old data can interpret it.
 *
 * The glass constitution (design-786 §2.10) governs: render canon, never
 * author state — no sentence on this page describes how things stand except
 * canon text carried verbatim; freshness is displayed (ratification dates on
 * every element), never auto-judged. Definitions appear verbatim from their
 * one home; this page restates no meaning (a definition has one home — the
 * CI second-definition scan's line).
 */

import { useState } from "react";
import {
  GLASS_CANON,
  type GlassRetiredEntry,
  type GlassRuledOutEntry,
  type GlassTerm,
} from "@command-center/shared";
import { cn } from "@/lib/utils";

/** The four ruled disposition groups, in the ruled-out file's own ruling
 *  order (#764 s03 F54–F57). Group headings name the disposition value —
 *  the ruled word, not a coinage. */
const DISPOSITION_ORDER = [
  "retired",
  "use-instead",
  "dictionary-register",
  "ordinary",
] as const;

export function GlassVocabularyPage() {
  return (
    <section className="mx-auto max-w-4xl p-6">
      <h1 className="mb-1 text-xl tracking-tight">The Glass — Vocabulary</h1>
      <p className="mb-1 text-sm text-plane-muted">
        The ratified glossary, the ruled-out words, and the retired ids,
        rendered verbatim from the canon files named below.
      </p>
      <p className="mb-6 text-xs text-plane-muted">
        Canon lives in <code>{GLASS_CANON.meta.sources.glossary}</code>,{" "}
        <code>{GLASS_CANON.meta.sources.ruledOut}</code> and{" "}
        <code>{GLASS_CANON.meta.sources.retired}</code>, packaged at build
        time; this page renders it read-only and defines nothing itself.
      </p>

      <GlossarySection />
      <RuledOutSection />
      <RetiredSection />
    </section>
  );
}

function SectionHeading({
  label,
  title,
  source,
}: {
  label: string;
  title: string;
  source: string;
}) {
  return (
    <div className="mb-3 mt-10 first:mt-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
        {label}
      </div>
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="text-xs text-plane-muted">
        renders <code>{source}</code>
      </p>
    </div>
  );
}

/** GLOSSARY — every ratified term, definition verbatim, grouped by the
 *  file's own categories in document order. */
function GlossarySection() {
  const { status, ratifiedOn, ratifiedBy, count, categories } =
    GLASS_CANON.vocabulary.glossary;

  return (
    <>
      <SectionHeading
        label="Glossary"
        title={`${count} ratified terms (${status} ${ratifiedOn}, ruled by ${ratifiedBy})`}
        source={GLASS_CANON.meta.sources.glossary}
      />
      {categories.map((cat) => (
        <div key={cat.id} className="mb-4 rounded-md border border-border bg-card">
          <div className="border-b border-border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
            {cat.id.replace(/_/g, " ")}
          </div>
          <dl>
            {cat.terms.map((t) => (
              <TermEntry key={t.term} term={t} />
            ))}
          </dl>
        </div>
      ))}
    </>
  );
}

function TermEntry({ term }: { term: GlassTerm }) {
  return (
    <div className="border-b border-border/60 px-3 py-2 last:border-b-0">
      <dt className="text-sm font-medium">
        {term.term}
        {term.aliases.length > 0 ? (
          <span className="ml-2 font-normal text-plane-muted">
            ({term.aliases.join(", ")})
          </span>
        ) : null}
        <span className="ml-2 text-xs font-normal text-plane-muted">
          ratified {term.ratifiedOn}
        </span>
      </dt>
      <dd className="mt-0.5 text-sm text-plane-muted">{term.definition}</dd>
      {term.avoid.length > 0 ? (
        <dd className="mt-0.5 text-xs text-plane-muted">
          avoid: {term.avoid.join(", ")}
        </dd>
      ) : null}
      {term.spokenAs !== null ? (
        <dd className="mt-0.5 text-xs text-plane-muted">
          spoken as {term.spokenAs}
          {term.preferInWriting !== null
            ? `; in writing, ${term.preferInWriting}`
            : null}
        </dd>
      ) : null}
    </div>
  );
}

/** RULED-OUT — the record of what was ruled out of the glossary, one group
 *  per ruled disposition. The file's own header (what_this_is and its
 *  amendment trail, the REOPENED record included) is carried verbatim. */
function RuledOutSection() {
  const { ruledOn, ruledBy, whatThisIs, whatThisIsNote, entries } =
    GLASS_CANON.vocabulary.ruledOut;

  return (
    <>
      <SectionHeading
        label="Ruled out"
        title={`${entries.length} words ruled out of the glossary (first ruled ${ruledOn} by ${ruledBy})`}
        source={GLASS_CANON.meta.sources.ruledOut}
      />
      <div className="mb-4 rounded-md border border-border bg-card p-3">
        <details>
          <summary className="cursor-pointer text-xs font-medium text-plane-muted">
            What this list is, and its amendment trail (verbatim from the
            canon file)
          </summary>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-xs text-plane-muted">
            {whatThisIs}
          </pre>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-xs text-plane-muted">
            {whatThisIsNote}
          </pre>
        </details>
      </div>
      {DISPOSITION_ORDER.map((disposition) => (
        <RuledOutGroup
          key={disposition}
          disposition={disposition}
          entries={entries.filter((e) => e.disposition === disposition)}
        />
      ))}
    </>
  );
}

function RuledOutGroup({
  disposition,
  entries,
}: {
  disposition: (typeof DISPOSITION_ORDER)[number];
  entries: GlassRuledOutEntry[];
}) {
  const [openTerm, setOpenTerm] = useState<string | null>(null);
  if (entries.length === 0) return null;

  return (
    <div className="mb-4 rounded-md border border-border bg-card">
      <div className="border-b border-border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
        {disposition} · {entries.length}
      </div>
      <ul>
        {entries.map((e) => {
          const open = openTerm === e.term;
          return (
            <li
              key={e.term}
              className={cn(
                "cursor-pointer border-b border-border/60 px-3 py-2 last:border-b-0 hover:bg-muted",
                open && "bg-muted",
              )}
              onClick={() => setOpenTerm(open ? null : e.term)}
            >
              <div className="text-sm">
                <span className="font-medium">{e.term}</span>
                {e.aliases.length > 0 ? (
                  <span className="ml-2 text-plane-muted">
                    ({e.aliases.join(", ")})
                  </span>
                ) : null}
                {e.ruledOn !== null ? (
                  <span className="ml-2 text-xs text-plane-muted">
                    ruled {e.ruledOn}
                  </span>
                ) : null}
              </div>
              {e.useInstead !== null ? (
                <div className="mt-0.5 text-xs text-plane-muted">
                  → {e.useInstead}
                </div>
              ) : null}
              {e.seeAlso !== null ? (
                <div className="mt-0.5 text-xs text-plane-muted">
                  see also: {e.seeAlso}
                </div>
              ) : null}
              {open ? (
                <p className="mt-1 whitespace-pre-wrap text-xs text-plane-muted">
                  {e.dispositionRuling}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** RETIRED — WAS USED, NOW NOT USED, DO NOT USE AGAIN; each entry carries
 *  what the dead id meant, verbatim from retired.ts. */
function RetiredSection() {
  const { entries } = GLASS_CANON.vocabulary.retired;

  return (
    <>
      <SectionHeading
        label="Retired"
        title={`${entries.length} retired ${entries.length === 1 ? "id" : "ids"}`}
        source={GLASS_CANON.meta.sources.retired}
      />
      {entries.map((e) => (
        <RetiredEntry key={e.id} entry={e} />
      ))}
    </>
  );
}

function RetiredEntry({ entry }: { entry: GlassRetiredEntry }) {
  return (
    <div className="mb-4 rounded-md border border-border bg-card p-3">
      <div className="text-sm">
        <code className="font-medium">{entry.id}</code>
        <span className="ml-2 text-xs text-plane-muted">
          {entry.kind} · retired {entry.ratifiedOn} · superseded by{" "}
          {entry.supersededBy ?? "nothing"}
        </span>
      </div>
      {entry.aliases.length > 0 ? (
        <div className="mt-0.5 text-xs text-plane-muted">
          aliases: {entry.aliases.join(", ")}
        </div>
      ) : null}
      <p className="mt-1 whitespace-pre-wrap text-xs">{entry.what}</p>
      <details className="mt-2 border-t border-border pt-2">
        <summary className="cursor-pointer text-xs font-medium text-plane-muted">
          Ruling rationale (verbatim from the canon file)
        </summary>
        <p className="mt-1 whitespace-pre-wrap text-xs text-plane-muted">
          {entry.rationale}
        </p>
      </details>
    </div>
  );
}
